import { useState, useEffect } from "react";

import { SelectField } from "../components/ui/select-field.js";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useMe } from "../context/MeContext.js";
import { ManagedInADP } from "../components/ManagedInADP.js";
import { DatePicker } from "../components/DatePicker.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { SpecularButton } from "../components/ui/specular-button.js";
import { usePageBack } from "../context/PageBackContext.js";

interface Project { id: string; code: string; name: string }
interface TimesheetEntry {
  id: string; date: string; hoursWorked: string; goalDescription: string | null; status: string;
  project: { id: string; code: string; name: string };
  employee: { firstName: string; lastName: string; employeeCode: string };
}

const statusColor = (s: string): React.CSSProperties => {
  if (s === "APPROVED")  return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "REJECTED")  return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  if (s === "SUBMITTED") return { background: "var(--color-info-bg-subtle)", color: "var(--color-info-fg)" };
  return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
};

function getMonday(d = new Date()): string {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(new Date(d).setDate(diff)).toISOString().slice(0, 10);
}

const inputStyle: React.CSSProperties = { padding: "8px 10px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: "14px", width: "100%", background: "var(--color-surface-0)" };

export default function TimesheetPage() {
  usePageBack("/dashboard");
  const { isUS } = useMe();
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ projectId: "", date: new Date().toISOString().slice(0, 10), hoursWorked: "8", goalDescription: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [weekStart, setWeekStart] = useState(getMonday());
  const [weekTotal, setWeekTotal] = useState(0);

  async function load() {
    setLoading(true);
    try {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const { data } = await api.get<{ data: TimesheetEntry[] }>(
        `/timesheets?from=${weekStart}&to=${weekEnd.toISOString().slice(0, 10)}&limit=50`
      );
      setEntries(data.data);
      setWeekTotal(data.data.reduce((s, e) => s + Number(e.hoursWorked), 0));
    } catch (err) { logError("TimesheetPage.load", err); } finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    api.get<{ data: Project[] }>("/employees/org-meta").then(() => {}).catch((err) => logError("TimesheetPage.loadOrgMeta", err));
    api.get<{ items?: Project[] }>("/timesheets?limit=1").then(() => {}).catch((err) => logError("TimesheetPage.loadTimesheetsStub", err));
  }, [weekStart]);

  // Load available projects for the form
  useEffect(() => {
    api.get<{ data: { project: Project }[] }>("/timesheets?limit=100&status=SUBMITTED")
      .then(({ data }) => {
        const seen = new Set<string>();
        const projs: Project[] = [];
        data.data.forEach((e) => { if (!seen.has(e.project.id)) { seen.add(e.project.id); projs.push(e.project); } });
        if (projs.length) setProjects(projs);
      }).catch((err) => logError("TimesheetPage.loadProjects", err));
  }, []);

  async function handleSubmit() {
    setSaving(true); setError("");
    try {
      await api.post("/timesheets", { ...form, hoursWorked: Number(form.hoursWorked) });
      setShowForm(false);
      setForm({ projectId: projects[0]?.id ?? "", date: new Date().toISOString().slice(0, 10), hoursWorked: "8", goalDescription: "" });
      load();
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Failed to submit");
      throw e;
    } finally { setSaving(false); }
  }

  async function handleApprove(id: string) {
    try { await api.patch(`/timesheets/${id}/approve`); load(); }
    catch (e: unknown) { alert(errMsg(e) ?? "Failed"); }
  }

  async function handleReject(id: string) {
    try { await api.patch(`/timesheets/${id}/reject`); load(); }
    catch (e: unknown) { alert(errMsg(e) ?? "Failed"); }
  }

  function shiftWeek(dir: -1 | 1) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  }

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  if (isUS) return <ManagedInADP module="Timesheets" />;
  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)" }}>
      <div style={{ background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)", padding: "var(--space-6) var(--space-8)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: 700 }}>Timesheets</h1>
          <p style={{ fontSize: "13px", color: "var(--color-surface-700)", marginTop: "2px" }}>
            {weekTotal.toFixed(1)}h logged this week
          </p>
        </div>
        <button onClick={() => { setShowForm(true); setError(""); }} style={{ padding: "8px 16px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: "14px" }}>
          + Log Hours
        </button>
      </div>

      {/* Week navigator */}
      <div style={{ padding: "var(--space-5) var(--space-8) 0", display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <SpecularButton onClick={() => shiftWeek(-1)}>← Prev</SpecularButton>
        <span style={{ fontSize: "14px", fontWeight: 600 }}>
          {new Date(weekStart).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} – {weekEnd.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
        <SpecularButton onClick={() => shiftWeek(1)}>Next →</SpecularButton>
        <SpecularButton onClick={() => setWeekStart(getMonday())} className="text-brand-600">This week</SpecularButton>
      </div>

      <div style={{ padding: "var(--space-4) var(--space-8) var(--space-8)" }}>
        {loading ? <div style={{ textAlign: "center", padding: "var(--space-12)", color: "var(--color-surface-700)" }}>Loading…</div> : (
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                  {["Employee", "Project", "Date", "Hours", "Goal", "Status", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-700)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0
                  ? <tr><td colSpan={7} style={{ padding: "var(--space-8)", textAlign: "center", color: "var(--color-surface-700)" }}>No entries for this week.</td></tr>
                  : entries.map((e, i) => (
                    <tr key={e.id} style={{ borderBottom: i < entries.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                      <td style={{ padding: "11px 14px" }}><div style={{ fontWeight: 600 }}>{e.employee.firstName} {e.employee.lastName}</div><div style={{ fontSize: "12px", color: "var(--color-surface-700)" }}>{e.employee.employeeCode}</div></td>
                      <td style={{ padding: "11px 14px" }}><div style={{ fontWeight: 500 }}>{e.project.name}</div><div style={{ fontSize: "11px", fontFamily: "var(--font-mono)", color: "var(--color-surface-700)" }}>{e.project.code}</div></td>
                      <td style={{ padding: "11px 14px", fontSize: "13px" }}>{new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                      <td style={{ padding: "11px 14px", fontWeight: 700, fontFamily: "var(--font-mono)" }}>{Number(e.hoursWorked).toFixed(1)}h</td>
                      <td style={{ padding: "11px 14px", fontSize: "12px", color: "var(--color-surface-700)", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.goalDescription ?? "—"}</td>
                      <td style={{ padding: "11px 14px" }}><span style={{ ...statusColor(e.status), padding: "2px 8px", borderRadius: "var(--radius-full)", fontSize: "11px", fontWeight: 600 }}>{e.status}</span></td>
                      <td style={{ padding: "11px 14px" }}>
                        {e.status === "SUBMITTED" && (
                          <div style={{ display: "flex", gap: "var(--space-2)" }}>
                            <button onClick={() => handleApprove(e.id)} style={{ padding: "3px 10px", background: "var(--color-success-bg)", color: "var(--color-success-fg)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>Approve</button>
                            <button onClick={() => handleReject(e.id)} style={{ padding: "3px 10px", background: "var(--color-error-bg)", color: "var(--color-error-fg)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>Reject</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Log hours slide-over */}
      {showForm && (
        <>
          <div onClick={() => setShowForm(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 40 }} />
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "400px", background: "var(--color-surface-0)", zIndex: 50, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.15)" }}>
            <div style={{ padding: "var(--space-6)", borderBottom: "1px solid var(--color-surface-200)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ fontSize: "17px", fontWeight: 700 }}>Log Hours</h2>
              <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "var(--color-surface-700)" }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
              {error && <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", fontSize: "13px" }}>{error}</div>}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-700)", display: "block", marginBottom: "4px" }}>Project ID *</label>
                {projects.length > 0
                  ? <SelectField value={form.projectId} onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}>
                      <option value="">Select project…</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                    </SelectField>
                  : <input style={inputStyle} placeholder="Project CUID" value={form.projectId} onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))} />
                }
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-700)", display: "block", marginBottom: "4px" }}>Date *</label>
                <DatePicker style={inputStyle} value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-700)", display: "block", marginBottom: "4px" }}>Hours *</label>
                <input type="number" step="0.5" min="0.5" max="24" style={inputStyle} value={form.hoursWorked} onChange={(e) => setForm((f) => ({ ...f, hoursWorked: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-surface-700)", display: "block", marginBottom: "4px" }}>Goal / Description</label>
                <textarea style={{ ...inputStyle, height: "80px", resize: "none" }} value={form.goalDescription} onChange={(e) => setForm((f) => ({ ...f, goalDescription: e.target.value }))} placeholder="What did you work on?" />
              </div>
            </div>
            <div style={{ padding: "var(--space-5) var(--space-6)", borderTop: "1px solid var(--color-surface-200)", display: "flex", gap: "var(--space-3)", justifyContent: "flex-end" }}>
              <button onClick={() => setShowForm(false)} style={{ padding: "8px 20px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", background: "none", cursor: "pointer", fontSize: "14px" }}>Cancel</button>
              <AsyncButton onClick={() => handleSubmit()} disabled={saving || !form.projectId || !form.date}>
                Submit
              </AsyncButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
